import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { Container, Row, Col, Card, Form, Button, Alert, Table } from 'react-bootstrap';
import { FaFileInvoiceDollar, FaSave, FaTimes, FaPlus, FaTrash } from 'react-icons/fa';
import api from '../../services/api';
import { toast } from 'react-toastify';
import Spinner from '../../components/layout/Spinner';

const InvoiceForm = () => {
  const { id, customerId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const prescriptionId = queryParams.get('prescription');
  
  const isEditMode = !!id;

  const [formData, setFormData] = useState({
    customer: customerId || '',
    prescription: prescriptionId || '',
    invoiceDate: new Date().toISOString().split('T')[0],
    dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    items: [{ description: '', quantity: 1, unitPrice: 0 }],
    taxRate: 0,
    discountValue: 0,
    discountType: 'percentage',
    notes: '',
    paymentStatus: 'pending',
    paymentMethod: '',
    paymentDate: '',
    amountPaid: 0
  });
  
  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(isEditMode);
  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const [loadingPrescriptions, setLoadingPrescriptions] = useState(!!customerId);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);
  const [subtotal, setSubtotal] = useState(0);
  const [taxAmount, setTaxAmount] = useState(0);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);

  useEffect(() => {
    fetchCustomers();
    if (isEditMode) {
      fetchInvoice();
    } else if (customerId) {
      fetchPrescriptionsForCustomer(customerId);
    }
  }, [id, customerId]);

  useEffect(() => {
    calculateTotals();
  }, [formData.items, formData.taxRate, formData.discountValue, formData.discountType]);

  const fetchCustomers = async () => {
    try {
            const res = await api.get('/customers', {
        params: {
          limit: 100,
          sortField: 'name',
          sortOrder: 'asc'
        }
      });
      setCustomers(res.data.customers);
      setLoadingCustomers(false);
    } catch (err) {
      console.error('Error fetching customers:', err);
      setError('Failed to load customers');
      setLoadingCustomers(false);
    }
  };

  const fetchPrescriptionsForCustomer = async (customerId) => {
    if (!customerId) {
      setPrescriptions([]);
      setLoadingPrescriptions(false);
      return;
    }
    
    try {
      setLoadingPrescriptions(true);
            const res = await api.get(`/prescriptions`, {
        params: {
          customer: customerId,
          limit: 100,
          sortField: 'prescriptionDate',
          sortOrder: 'desc'
        }
      });
      setPrescriptions(res.data.prescriptions);
      setLoadingPrescriptions(false);
    } catch (err) {
      console.error('Error fetching prescriptions:', err);
      setLoadingPrescriptions(false);
    }
  };

  const fetchInvoice = async () => {
    try {
            const res = await api.get(`/invoices/${id}`);
      
      // Format dates for form inputs
      const invoiceDate = res.data.invoiceDate ? 
        new Date(res.data.invoiceDate).toISOString().split('T')[0] : '';
      
      const dueDate = res.data.dueDate ? 
        new Date(res.data.dueDate).toISOString().split('T')[0] : '';
      
      const paymentDate = res.data.paymentDate ? 
        new Date(res.data.paymentDate).toISOString().split('T')[0] : '';
      
      setFormData({
        customer: res.data.customer._id,
        prescription: res.data.prescription ? res.data.prescription._id : '',
        invoiceDate,
        dueDate,
        items: res.data.items.map(item => ({
          description: item.description,
          quantity: item.quantity,
          unitPrice: item.unitPrice
        })),
        taxRate: res.data.taxRate || 0,
        discountValue: res.data.discountValue || 0,
        discountType: res.data.discountType || 'percentage',
        notes: res.data.notes || '',
        paymentStatus: res.data.paymentStatus || 'pending',
        paymentMethod: res.data.paymentMethod || '',
        paymentDate,
        amountPaid: res.data.amountPaid || 0
      });
      
      if (res.data.customer._id) {
        fetchPrescriptionsForCustomer(res.data.customer._id);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching invoice:', err);
      setError('Failed to load invoice data');
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear field error when user types
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }
  };

  const handleCustomerChange = (e) => {
    const customerId = e.target.value;
    setFormData({
      ...formData,
      customer: customerId,
      prescription: '' // Reset prescription when customer changes
    });
    
    if (customerId) {
      fetchPrescriptionsForCustomer(customerId);
    } else {
      setPrescriptions([]);
    }
    
    if (formErrors.customer) {
      setFormErrors({
        ...formErrors,
        customer: ''
      });
    }
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...formData.items];
    updatedItems[index][field] = value;
    
    setFormData({
      ...formData,
      items: updatedItems
    });
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: '', quantity: 1, unitPrice: 0 }]
    });
  };

  const removeItem = (index) => {
    if (formData.items.length === 1) {
      toast.error('Invoice must have at least one item');
      return;
    }
    
    const updatedItems = formData.items.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      items: updatedItems
    });
  };

  const calculateTotals = () => {
    // Calculate subtotal
    const calculatedSubtotal = formData.items.reduce((sum, item) => {
      return sum + (item.quantity * item.unitPrice);
    }, 0);
    
    // Calculate tax amount
    const calculatedTaxAmount = calculatedSubtotal * (formData.taxRate / 100);
    
    // Calculate discount amount
    let calculatedDiscountAmount = 0;
    if (formData.discountType === 'percentage') {
      calculatedDiscountAmount = calculatedSubtotal * (formData.discountValue / 100);
    } else {
      calculatedDiscountAmount = formData.discountValue;
    }
    
    // Calculate total
    const calculatedTotal = calculatedSubtotal + calculatedTaxAmount - calculatedDiscountAmount;
    
    setSubtotal(calculatedSubtotal);
    setTaxAmount(calculatedTaxAmount);
    setDiscountAmount(calculatedDiscountAmount);
    setTotalAmount(calculatedTotal);
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.customer) {
      errors.customer = 'Customer is required';
    }
    
    if (!formData.invoiceDate) {
      errors.invoiceDate = 'Invoice date is required';
    }
    
    // Validate items
    const itemErrors = [];
    formData.items.forEach((item, index) => {
      const itemError = {};
      
      if (!item.description.trim()) {
        itemError.description = 'Description is required';
      }
      
      if (item.quantity <= 0) {
        itemError.quantity = 'Quantity must be greater than 0';
      }
      
      if (item.unitPrice < 0) {
        itemError.unitPrice = 'Unit price cannot be negative';
      }
      
      if (Object.keys(itemError).length > 0) {
        itemErrors[index] = itemError;
      }
    });
    
    if (itemErrors.length > 0) {
      errors.items = itemErrors;
    }
    
    // Validate payment details if status is not pending
    if (formData.paymentStatus !== 'pending') {
      if (!formData.paymentMethod) {
        errors.paymentMethod = 'Payment method is required for non-pending invoices';
      }
      
      if (formData.paymentStatus === 'paid' && formData.amountPaid <= 0) {
        errors.amountPaid = 'Amount paid must be greater than 0 for paid invoices';
      }
      
      if (formData.paymentStatus === 'partial' && formData.amountPaid <= 0) {
        errors.amountPaid = 'Amount paid must be greater than 0 for partially paid invoices';
      }
      
      if (formData.paymentStatus === 'partial' && formData.amountPaid >= totalAmount) {
        errors.amountPaid = 'Amount paid must be less than total amount for partially paid invoices';
      }
      
      if (formData.paymentStatus === 'paid' && formData.amountPaid < totalAmount) {
        errors.amountPaid = 'Amount paid must be at least equal to total amount for paid invoices';
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Format items to match server expectations
      const formattedItems = formData.items.map(item => ({
        name: item.description,
        quantity: Number(item.quantity),
        unitPrice: Number(item.unitPrice),
        amount: Number(item.quantity) * Number(item.unitPrice)
      }));
      
      // Prepare data in the format expected by the server
      const dataToSubmit = {
        customer: formData.customer,
        prescription: formData.prescription || null,
        items: formattedItems,
        subtotal: subtotal,
        taxRate: Number(formData.taxRate) || 0,
        taxAmount: taxAmount || 0,
        discountRate: formData.discountType === 'percentage' ? Number(formData.discountValue) || 0 : 0,
        discountAmount: discountAmount || 0,
        total: totalAmount,
        paymentStatus: formData.paymentStatus || 'pending',
        paymentMethod: formData.paymentMethod || (formData.paymentStatus !== 'pending' ? 'cash' : ''),
        notes: formData.notes || ''
      };
      
      // Add payment date if available
      if (formData.paymentDate) {
        dataToSubmit.paymentDate = formData.paymentDate;
      }
      
      // Add amount paid if available
      if (formData.amountPaid) {
        dataToSubmit.amountPaid = Number(formData.amountPaid);
      }
      
      console.log('Submitting invoice data:', dataToSubmit);
      
      // Ensure all required fields are present and properly formatted
      if (!dataToSubmit.customer) {
        throw new Error('Customer is required');
      }
      
      if (!dataToSubmit.items || dataToSubmit.items.length === 0) {
        throw new Error('At least one item is required');
      }
      
      // Ensure all items have required fields
      dataToSubmit.items.forEach((item, index) => {
        if (!item.name) {
          throw new Error(`Item ${index + 1} is missing a name`);
        }
        if (typeof item.quantity !== 'number' || item.quantity <= 0) {
          throw new Error(`Item ${index + 1} has an invalid quantity`);
        }
        if (typeof item.unitPrice !== 'number' || item.unitPrice < 0) {
          throw new Error(`Item ${index + 1} has an invalid unit price`);
        }
        if (typeof item.amount !== 'number' || item.amount < 0) {
          throw new Error(`Item ${index + 1} has an invalid amount`);
        }
      });
      
      if (isEditMode) {
                await api.put(`/invoices/${id}`, dataToSubmit);
        toast.success('Invoice updated successfully');
        navigate(`/invoices/${id}`);
      } else {
                const res = await api.post('/invoices', dataToSubmit);
        toast.success('Invoice created successfully');
        navigate(`/invoices/${res.data.invoice._id}`);
      }
    } catch (err) {
      console.error('Error saving invoice:', err);
      setError(err.response?.data?.msg || 'Failed to save invoice');
      toast.error(err.response?.data?.msg || 'Failed to save invoice');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading || loadingCustomers) {
    return <Spinner />;
  }

  return (
    <Container>
      <Row className="mb-4 align-items-center">
        <Col>
          <h1>
            <FaFileInvoiceDollar className="me-2" />
            {isEditMode ? 'Edit Invoice' : 'Create New Invoice'}
          </h1>
        </Col>
      </Row>
      
      {error && <Alert variant="danger">{error}</Alert>}
      
      <Form onSubmit={handleSubmit}>
        <Card className="mb-4">
          <Card.Header>Basic Information</Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="customer">
                  <Form.Label>Customer <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    name="customer"
                    value={formData.customer}
                    onChange={handleCustomerChange}
                    isInvalid={!!formErrors.customer}
                    disabled={!!customerId || isEditMode}
                  >
                    <option value="">Select Customer</option>
                    {customers.map(customer => (
                      <option key={customer._id} value={customer._id}>
                        {customer.name}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {formErrors.customer}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              
              <Col md={6}>
                <Form.Group className="mb-3" controlId="prescription">
                  <Form.Label>Related Prescription</Form.Label>
                  <Form.Select
                    name="prescription"
                    value={formData.prescription}
                    onChange={handleChange}
                    disabled={loadingPrescriptions || !formData.customer || isEditMode}
                  >
                    <option value="">None</option>
                    {prescriptions.map(prescription => (
                      <option key={prescription._id} value={prescription._id}>
                        {new Date(prescription.prescriptionDate).toLocaleDateString()} - {prescription.prescriptionType}
                      </option>
                    ))}
                  </Form.Select>
                  {loadingPrescriptions && formData.customer && (
                    <Form.Text className="text-muted">
                      Loading prescriptions...
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3" controlId="invoiceDate">
                  <Form.Label>Invoice Date <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="date"
                    name="invoiceDate"
                    value={formData.invoiceDate}
                    onChange={handleChange}
                    isInvalid={!!formErrors.invoiceDate}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formErrors.invoiceDate}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              
              <Col md={6}>
                <Form.Group className="mb-3" controlId="dueDate">
                  <Form.Label>Due Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="dueDate"
                    value={formData.dueDate}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>
        
        <Card className="mb-4">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <div>Invoice Items</div>
            <Button variant="success" size="sm" onClick={addItem}>
              <FaPlus className="me-1" /> Add Item
            </Button>
          </Card.Header>
          <Card.Body>
            <div className="table-responsive">
              <Table>
                <thead>
                  <tr>
                    <th style={{ width: '50%' }}>Description</th>
                    <th style={{ width: '15%' }}>Quantity</th>
                    <th style={{ width: '20%' }}>Unit Price ($)</th>
                    <th style={{ width: '15%' }}>Total</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {formData.items.map((item, index) => (
                    <tr key={index}>
                      <td>
                        <Form.Control
                          type="text"
                          value={item.description}
                          onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                          placeholder="Item description"
                          isInvalid={formErrors.items && formErrors.items[index]?.description}
                        />
                        {formErrors.items && formErrors.items[index]?.description && (
                          <Form.Control.Feedback type="invalid">
                            {formErrors.items[index].description}
                          </Form.Control.Feedback>
                        )}
                      </td>
                      <td>
                        <Form.Control
                          type="number"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(index, 'quantity', parseInt(e.target.value) || 0)}
                          min="1"
                          isInvalid={formErrors.items && formErrors.items[index]?.quantity}
                        />
                        {formErrors.items && formErrors.items[index]?.quantity && (
                          <Form.Control.Feedback type="invalid">
                            {formErrors.items[index].quantity}
                          </Form.Control.Feedback>
                        )}
                      </td>
                      <td>
                        <Form.Control
                          type="number"
                          value={item.unitPrice}
                          onChange={(e) => handleItemChange(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                          min="0"
                          step="0.01"
                          isInvalid={formErrors.items && formErrors.items[index]?.unitPrice}
                        />
                        {formErrors.items && formErrors.items[index]?.unitPrice && (
                          <Form.Control.Feedback type="invalid">
                            {formErrors.items[index].unitPrice}
                          </Form.Control.Feedback>
                        )}
                      </td>
                      <td>
                        ${(item.quantity * item.unitPrice).toFixed(2)}
                      </td>
                      <td>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => removeItem(index)}
                        >
                          <FaTrash />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
        
        <Card className="mb-4">
          <Card.Header>Invoice Totals</Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group controlId="taxRate">
                      <Form.Label>Tax Rate (%)</Form.Label>
                      <Form.Control
                        type="number"
                        name="taxRate"
                        value={formData.taxRate}
                        onChange={handleChange}
                        min="0"
                        step="0.01"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group controlId="discountValue">
                      <Form.Label>Discount</Form.Label>
                      <div className="d-flex">
                        <Form.Control
                          type="number"
                          name="discountValue"
                          value={formData.discountValue}
                          onChange={handleChange}
                          min="0"
                          step="0.01"
                          className="me-2"
                        />
                        <Form.Select
                          name="discountType"
                          value={formData.discountType}
                          onChange={handleChange}
                          style={{ width: '80px' }}
                        >
                          <option value="percentage">%</option>
                          <option value="fixed">$</option>
                        </Form.Select>
                      </div>
                    </Form.Group>
                  </Col>
                </Row>
                
                <Form.Group className="mb-3" controlId="notes">
                  <Form.Label>Notes</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    placeholder="Enter any additional notes for the invoice"
                  />
                </Form.Group>
              </Col>
              
              <Col md={6}>
                <Card className="bg-light">
                  <Card.Body>
                    <div className="d-flex justify-content-between mb-2">
                      <span>Subtotal:</span>
                      <span>${subtotal.toFixed(2)}</span>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span>Tax ({formData.taxRate}%):</span>
                      <span>${taxAmount.toFixed(2)}</span>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span>Discount:</span>
                      <span>${discountAmount.toFixed(2)}</span>
                    </div>
                    <hr />
                    <div className="d-flex justify-content-between fw-bold">
                      <span>Total:</span>
                      <span>${totalAmount.toFixed(2)}</span>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Card.Body>
        </Card>
        
        <Card className="mb-4">
          <Card.Header>Payment Information</Card.Header>
          <Card.Body>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3" controlId="paymentStatus">
                  <Form.Label>Payment Status</Form.Label>
                  <Form.Select
                    name="paymentStatus"
                    value={formData.paymentStatus}
                    onChange={handleChange}
                  >
                    <option value="pending">Pending</option>
                    <option value="partial">Partially Paid</option>
                    <option value="paid">Paid</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              
              {formData.paymentStatus !== 'pending' && (
                <>
                  <Col md={4}>
                    <Form.Group className="mb-3" controlId="paymentMethod">
                      <Form.Label>Payment Method</Form.Label>
                      <Form.Select
                        name="paymentMethod"
                        value={formData.paymentMethod}
                        onChange={handleChange}
                        isInvalid={!!formErrors.paymentMethod}
                      >
                        <option value="">Select Payment Method</option>
                        <option value="cash">Cash</option>
                        <option value="credit_card">Credit Card</option>
                        <option value="debit_card">Debit Card</option>
                        <option value="bank_transfer">Bank Transfer</option>
                        <option value="check">Check</option>
                        <option value="other">Other</option>
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {formErrors.paymentMethod}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  
                  <Col md={4}>
                    <Form.Group className="mb-3" controlId="paymentDate">
                      <Form.Label>Payment Date</Form.Label>
                      <Form.Control
                        type="date"
                        name="paymentDate"
                        value={formData.paymentDate}
                        onChange={handleChange}
                      />
                    </Form.Group>
                  </Col>
                  
                  <Col md={4}>
                    <Form.Group className="mb-3" controlId="amountPaid">
                      <Form.Label>Amount Paid ($)</Form.Label>
                      <Form.Control
                        type="number"
                        name="amountPaid"
                        value={formData.amountPaid}
                        onChange={handleChange}
                        min="0"
                        step="0.01"
                        isInvalid={!!formErrors.amountPaid}
                      />
                      <Form.Control.Feedback type="invalid">
                        {formErrors.amountPaid}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </>
              )}
            </Row>
          </Card.Body>
        </Card>
        
        <div className="d-flex justify-content-end mb-4">
          <Button 
            variant="secondary" 
            as={Link} 
            to={isEditMode ? `/invoices/${id}` : '/invoices'} 
            className="me-2"
          >
            <FaTimes className="me-1" /> Cancel
          </Button>
          <Button 
            variant="primary" 
            type="submit" 
            disabled={isSubmitting}
          >
            <FaSave className="me-1" /> {isSubmitting ? 'Saving...' : 'Save Invoice'}
          </Button>
        </div>
      </Form>
    </Container>
  );
};

export default InvoiceForm;
